import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  Users,
  MapPin,
  ExternalLink,
  ChevronDown,
  Star,
  CheckCircle,
  Car,
  Calendar,
  DollarSign,
  Target,
  Instagram,
  Globe,
  X,
  Flag,
  Compass,
  Trophy
} from 'lucide-react'
import {
  DRIVING_CLUBS,
  CLUB_TYPES,
  CLUB_FOCUSES,
  getClubsByType,
  getFeaturedClubs
} from '../../lib/drivingClubs'

// Type icons
const TYPE_ICONS = {
  touring: Compass,
  concours: Trophy,
  track: Flag,
  marque: Car,
  social: Users,
  vintage: Star
}

const ClubCard = ({ club }) => {
  const [expanded, setExpanded] = useState(false)
  const TypeIcon = TYPE_ICONS[club.type] || Users

  return (
    <div className={`card overflow-hidden ${club.featured ? 'ring-1 ring-rdc-orange/20' : ''}`}>
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="section-title text-base">{club.name}</h3>
              {club.verified && (
                <span className="flex items-center gap-1 text-xs text-rdc-forest bg-rdc-forest/10 px-2 py-0.5 rounded-full">
                  <CheckCircle size={10} />
                  Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm text-rdc-taupe">
              <span className="flex items-center gap-1.5">
                <MapPin size={13} />
                {club.region}
              </span>
              <span className="flex items-center gap-1.5 capitalize">
                <TypeIcon size={13} />
                {club.type}
              </span>
            </div>
          </div>
          {club.featured && (
            <span className="text-xs font-semibold text-rdc-orange bg-rdc-orange/8 px-2.5 py-1 rounded-full whitespace-nowrap">
              Featured
            </span>
          )}
        </div>

        <p className="text-sm text-rdc-charcoal mb-4 leading-relaxed">{club.description}</p>

        {/* Focus Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {club.focus.slice(0, 3).map(f => (
            <span
              key={f}
              className="text-xs px-2.5 py-1 bg-rdc-cream text-rdc-charcoal rounded-full font-medium"
            >
              {f}
            </span>
          ))}
          {club.focus.length > 3 && (
            <span className="text-xs px-2 py-1 text-rdc-taupe">
              +{club.focus.length - 3} more
            </span>
          )}
        </div>

        {/* Quick Info */}
        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          {club.membershipCost && (
            <div className="flex items-center gap-2 text-rdc-charcoal">
              <DollarSign size={14} className="text-rdc-taupe" />
              <span>{club.membershipCost}</span>
            </div>
          )}
          {club.members && (
            <div className="flex items-center gap-2 text-rdc-charcoal">
              <Users size={14} className="text-rdc-taupe" />
              <span>{club.members}</span>
            </div>
          )}
        </div>

        {/* Expandable Section */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs text-rdc-orange font-medium hover:underline"
        >
          {expanded ? 'Show less' : 'More details'}
          <ChevronDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-rdc-stone/30 space-y-3 animate-fade-in">
            {/* Vibe */}
            {club.vibe && (
              <div className="p-3 bg-rdc-cream/60 rounded-lg">
                <div className="text-xs font-semibold text-rdc-taupe uppercase tracking-wider mb-1">The Vibe</div>
                <p className="text-sm text-rdc-charcoal italic">{club.vibe}</p>
              </div>
            )}

            {/* Ideal For */}
            {club.idealFor && (
              <div className="p-3 bg-rdc-forest/5 rounded-lg">
                <div className="text-xs font-semibold text-rdc-forest uppercase tracking-wider mb-1">Best For</div>
                <p className="text-sm text-rdc-charcoal">{club.idealFor}</p>
              </div>
            )}

            {/* Events */}
            {club.events && (
              <div className="flex items-center gap-2 text-sm text-rdc-charcoal">
                <Calendar size={14} className="text-rdc-taupe" />
                <span>{club.events}</span>
              </div>
            )}

            {/* Membership Type */}
            {club.membershipType && (
              <div className="flex items-center gap-2 text-sm text-rdc-charcoal">
                <Target size={14} className="text-rdc-taupe" />
                <span>{club.membershipType}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-4 bg-rdc-cream/30 border-t border-rdc-stone/20 flex items-center gap-4">
        {club.website && (
          <a
            href={club.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm text-rdc-orange hover:underline font-medium"
          >
            <Globe size={14} />
            Website
            <ExternalLink size={11} />
          </a>
        )}
        {club.instagram && (
          <a
            href={`https://instagram.com/${club.instagram.replace('@', '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm text-rdc-charcoal hover:text-rdc-orange"
          >
            <Instagram size={14} />
            {club.instagram}
          </a>
        )}
      </div>
    </div>
  )
}

export default function Clubs() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedFocus, setSelectedFocus] = useState('All Makes')
  const [showFilters, setShowFilters] = useState(false)

  // Filter clubs
  const getFilteredClubs = () => {
    let clubs = [...DRIVING_CLUBS]

    if (selectedType !== 'all') {
      clubs = clubs.filter(c => c.type === selectedType)
    }

    if (selectedFocus !== 'All Makes') {
      clubs = clubs.filter(c => c.focus.includes(selectedFocus))
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      clubs = clubs.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.region.toLowerCase().includes(q) ||
        c.focus.some(f => f.toLowerCase().includes(q))
      )
    }

    return clubs
  }

  const filteredClubs = getFilteredClubs()
  const featuredClubs = filteredClubs.filter(c => c.featured)
  const otherClubs = filteredClubs.filter(c => !c.featured)

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedType('all')
    setSelectedFocus('All Makes')
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <p className="text-label mb-1">Connect</p>
        <h1 className="page-title">Driving & Motoring Clubs</h1>
        <p className="text-body mt-2">
          Find your community—from casual Cars & Coffee to exclusive rally clubs
        </p>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-rdc-taupe" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search clubs by name, type, or region..."
            className="input-field pl-11"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-secondary flex items-center gap-2 ${showFilters ? 'bg-rdc-cream' : ''}`}
        >
          <Users size={16} />
          Filters
          <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Active Filters */}
      {(selectedType !== 'all' || selectedFocus !== 'All Makes') && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-xs text-rdc-taupe">Active filters:</span>
          {selectedType !== 'all' && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-rdc-orange/10 text-rdc-orange rounded-full">
              {CLUB_TYPES.find(t => t.id === selectedType)?.label}
              <button onClick={() => setSelectedType('all')} className="hover:text-rdc-orange/70">
                <X size={12} />
              </button>
            </span>
          )}
          {selectedFocus !== 'All Makes' && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-rdc-forest/10 text-rdc-forest rounded-full">
              {selectedFocus}
              <button onClick={() => setSelectedFocus('All Makes')} className="hover:text-rdc-forest/70">
                <X size={12} />
              </button>
            </span>
          )}
          <button
            onClick={clearFilters}
            className="text-xs text-rdc-taupe hover:text-rdc-charcoal underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="card p-5 mb-6 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="form-label">Focus / Marque</label>
              <select
                value={selectedFocus}
                onChange={(e) => setSelectedFocus(e.target.value)}
                className="input-field"
              >
                {CLUB_FOCUSES.map(focus => (
                  <option key={focus} value={focus}>{focus}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Type Pills */}
      <div className="flex gap-2 overflow-x-auto pb-4 mb-6 -mx-2 px-2 scrollbar-hide">
        {CLUB_TYPES.map(type => (
          <button
            key={type.id}
            onClick={() => setSelectedType(type.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              selectedType === type.id
                ? 'bg-rdc-orange text-white shadow-sm'
                : 'bg-white text-rdc-charcoal hover:bg-rdc-cream border border-rdc-stone/50'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-rdc-taupe">
          {filteredClubs.length} club{filteredClubs.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Results */}
      {filteredClubs.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-rdc-cream flex items-center justify-center">
            <Users size={28} className="text-rdc-taupe" strokeWidth={1.5} />
          </div>
          <h3 className="section-title mb-2">No clubs found</h3>
          <p className="text-body text-rdc-charcoal mb-6">
            Try adjusting your search or filters
          </p>
          <button onClick={clearFilters} className="btn-secondary">
            Clear All Filters
          </button>
        </div>
      ) : (
        <>
          {/* Featured Clubs */}
          {featuredClubs.length > 0 && (
            <div className="mb-10">
              <h2 className="flex items-center gap-2 text-label mb-4">
                <Star size={14} className="text-rdc-orange" />
                Recommended Clubs
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {featuredClubs.map(club => (
                  <ClubCard key={club.id} club={club} />
                ))}
              </div>
            </div>
          )}

          {/* All Clubs */}
          {otherClubs.length > 0 && (
            <div>
              <h2 className="text-label mb-4">
                All Clubs
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {otherClubs.map(club => (
                  <ClubCard key={club.id} club={club} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Submit Club CTA */}
      <div className="mt-12 card p-8 bg-gradient-to-br from-rdc-cream via-white to-rdc-cream text-center">
        <h3 className="section-title mb-2">Know a great club we're missing?</h3>
        <p className="text-body text-rdc-charcoal mb-5">
          Help fellow enthusiasts by suggesting clubs you're part of
        </p>
        <button className="btn-primary">
          Suggest a Club
        </button>
      </div>
    </div>
  )
}
